/**
 * Browser-side latency probe shared by the location cards and the world map.
 *
 * A `no-cors` fetch still settles when the response arrives, so timing one is a
 * usable round trip without the target needing CORS headers. What the figure is
 * worth depends on a few details:
 *
 * - The probe URL must be region-pinned. Anything behind an anycast CDN is
 *   answered by whichever edge is nearest the visitor, so two locations sharing
 *   one CDN-fronted URL report the same figure — the distance to the CDN, not to
 *   either location.
 * - The probe URL must answer directly. `no-cors` forces `redirect: "follow"`
 *   (Chrome rejects any other mode outright), so a URL that redirects — as
 *   aurex.lk/favicon.ico does, to www, and as an S3 endpoint's bare `/` does, to
 *   aws.amazon.com — gets timed as two or more round trips, the second landing
 *   somewhere unrelated. That read about four times the real RTT.
 * - The first round trip to a host pays for DNS and the TLS handshake, so
 *   callers discard it and keep the fastest of the rest.
 * - Probes that race the page's own downloads read several times high, which is
 *   what `whenIdle` is for.
 * - Each request is capped by `TIMEOUT_MS`. A host that accepts connections but
 *   never answers would otherwise hold the sequential probe loop open for the
 *   browser's own timeout, leaving every later location stuck on "measuring".
 */
const TIMEOUT_MS = 6000;

/** One timed round trip in ms. Rejects if the probe never answers. */
export async function probe(url: string): Promise<number> {
    const abort = new AbortController();
    const cap = setTimeout(() => abort.abort(), TIMEOUT_MS);
    const t0 = performance.now();
    try {
        await fetch(`${url}${url.includes("?") ? "&" : "?"}t=${Date.now()}-${Math.random()}`, {
            mode: "no-cors",
            cache: "no-store",
            signal: abort.signal,
        });
    } finally {
        clearTimeout(cap);
    }
    return performance.now() - t0;
}

/** Fastest of `samples` round trips, after a discarded warm-up. */
export async function measureLatency(url: string, samples = 3): Promise<number> {
    const times: number[] = [];
    for (let i = 0; i <= samples; i++) {
        const ms = await probe(url);
        if (i > 0) times.push(ms);
    }
    return Math.round(Math.min(...times));
}

/** Resolves once the page has stopped competing for the connection. */
export function whenIdle(): Promise<void> {
    return new Promise((resolve) => {
        const beat = () => setTimeout(resolve, 400);
        if (document.readyState === "complete") beat();
        else window.addEventListener("load", beat, { once: true });
    });
}

/** Automated visitors skew perf audits and gain nothing from a live probe. */
export function isAutomated(): boolean {
    if (typeof navigator === "undefined") return true;
    const ua = navigator.userAgent;
    return (
        navigator.webdriver ||
        /Lighthouse|Chrome-Lighthouse|HeadlessChrome|Page Speed|Speed Insights|GTmetrix|PTST/i.test(ua)
    );
}
